import classNames from 'classnames'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import Alert from 'admin/components/common/Alert'
import PageFrame from 'admin/components/common/PageFrame'
import Text from 'admin/components/common/Text'
import ValidationMessage from 'admin/components/common/ValidationMessage'
import {
  withContext,
  WithContextProps,
} from 'admin/components/common/withContext'
import { combineValidators } from 'ansok/components/common/CombineValidators'
import FormField from 'components/ui/FormField'
import FormFieldTextInput from 'components/ui/FormFieldTextInput'
import Loader from 'components/ui/Loader'
import {
  validateEmail,
  validatePersonalId,
  validateRequired,
  validateTelephoneNumber
} from 'functions/validators'
import { ErrorType } from 'types'

/**
 * Common person form component
 */
const PersonForm: React.FunctionComponent<ComponentProps> = props => {
  const {
    errors,
    pageTitle,
    backLink,
    showKimIdField,
    submitButtonText,
    locale,
    onSubmit,
    newPerson,
    isLoading,
  } = props

  const { control, handleSubmit } = useFormContext(); 

  return (
    <PageFrame>
      <div className="Box">
        <Loader transparent local visible={isLoading} />
        {pageTitle}
        <Alert className="l-margin--b" alert={errors} />
        {backLink && <p>{backLink}</p>}
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="l-columns">
            <div className="l-flex l-flex--b50">
              {showKimIdField && (
                <Controller
                  control={control}
                  name="kimId"
                  rules={{
                    validate: validateRequired,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <label htmlFor="kimId">
                        <Text>general.kim_id</Text>
                      </label>
                      <input
                        className={classNames(error?.type && 'input--invalid')}
                        data-testid={`text_${field.name}`}
                        name={field.name}
                        {...field}
                        maxLength={12}
                        id="kimId"
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/[^0-9]/g, '')
                          field.onChange(e)
                        }}
                        type="text"
                      />
                      {error && <ValidationMessage error={error.message} />}
                    </div>
                  )}
                />
              )}
              <FormField
                ValidationMessageComponent={ValidationMessage}
                TextComponent={Text}
                control={control}
                name="personalId"
                label="general.personal_id"
                maxLength={15}
                validate={newPerson ? validatePersonalId : false}
                disabled={!newPerson}
                placeholder={locale.translate('label.personal_id_placeholder')}
                autoComplete="new-password"
              />
              <FormFieldTextInput
                ValidationMessageComponent={ValidationMessage}
                TextComponent={Text}
                control={control}
                name="firstName"
                label="general.first_name"
                maxLength={50}
                autoComplete="given-name"
              />
              <FormFieldTextInput
                ValidationMessageComponent={ValidationMessage}
                TextComponent={Text}
                control={control}
                name="lastName"
                label="general.last_name"
                maxLength={50}
                autoComplete="family-name"
              />
            </div>
            <div className="l-flex__spacer--5" />
            <div className="l-flex l-flex--b50">
              <FormField
                ValidationMessageComponent={ValidationMessage}
                TextComponent={Text}
                control={control}
                name="email"
                label="general.email"
                maxLength={254}
                validate={validateEmail}
                autoComplete="email"
              />
              <FormField
                ValidationMessageComponent={ValidationMessage}
                TextComponent={Text}
                control={control}
                name="telephoneNumber"
                label="general.mobile_number_ex_010"
                placeholder={locale.translate(
                  'label.mobile_number_placeholder'
                )}
                maxLength={20}
                validate={combineValidators([validateRequired, validateTelephoneNumber])}
                autoComplete="tel"
              />
            </div>
          </div>
          <div className="l-align--center">
            <button type="submit">{submitButtonText}</button>
          </div>
        </form>
      </div>
    </PageFrame>
  )
}

PersonForm.defaultProps = {
  showKimIdField: false,
}

type ComponentProps = React.HTMLProps<HTMLFormElement> &
  PersonFormProps &
  WithContextProps

type PersonFormProps = {
  pageTitle: React.ReactNode
  backLink?: React.ReactNode
  showKimIdField: boolean
  submitButtonText: React.ReactNode | string
  errors?: ErrorType
  newPerson: boolean
  isLoading?: boolean
  onSubmit: (data: PersonFormData) => void
  initialValues: PersonFormData
}

export type PersonFormData = {
  kimId?: number
  personalId?: string
  firstName: string
  lastName: string
  email: string
  telephoneNumber: string
}

export default withContext(PersonForm)
